<template>
  <div class="parameters-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h2 class="page-title">
            <i class="fas fa-cog title-icon" />
            Paramètres
          </h2>
          <p class="page-subtitle">
            Frais société
          </p>
        </div>
      </div>
    </div>

    <!-- Delivery Fee Configuration Card -->
    <b-card class="config-card">
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-truck icon" />
          <h4 class="mb-0">
            Frais de société de livraison
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Définissez les frais de société de livraison qui seront automatiquement soustraits
          du "Net à payer" lors de la création des factures. Ces frais peuvent être modifiés
          individuellement pour chaque facture si nécessaire.
        </p>

        <b-form @submit.prevent="saveDeliveryFee">
          <b-form-group
            label="Montant des frais (DT)"
            label-for="delivery-fee"
            class="form-group-enhanced"
          >
            <div class="input-wrapper">
              <b-form-input
                id="delivery-fee"
                v-model.number="deliveryFee"
                type="number"
                step="0.001"
                min="0"
                placeholder="0.000"
                class="form-input-enhanced"
                :state="deliveryFee >= 0 ? true : false"
              />
              <span class="input-suffix">DT</span>
            </div>
            <small class="text-muted">
              Entrez 0 pour ne pas appliquer de frais par défaut
            </small>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loading || deliveryFee < 0"
              class="save-btn"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <!-- Current Value Display -->
        <div
          v-if="!loading && currentFee !== null"
          class="current-value-display"
        >
          <i class="fas fa-info-circle" />
          <span>Valeur actuelle : <strong>{{ formatCurrency(currentFee) }}</strong></span>
        </div>
      </div>
    </b-card>

    <!-- Additional Parameters Section (for future expansion) -->
    <b-card class="info-card">
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-info-circle icon" />
          <h4 class="mb-0">
            Informations
          </h4>
        </div>
      </template>

      <div class="info-content">
        <ul class="info-list">
          <li>
            <i class="fas fa-check-circle" />
            Les frais de société de livraison sont appliqués automatiquement aux nouvelles factures
          </li>
          <li>
            <i class="fas fa-check-circle" />
            Les frais peuvent être modifiés individuellement pour chaque facture
          </li>
        </ul>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BButton, BFormGroup, BFormInput, BForm, BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'Parameters',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BForm,
    BSpinner,
  },
  data() {
    return {
      deliveryFee: 0,
      currentFee: null,
      loading: false,
    }
  },
  computed: {
    isAdmin() {
      const role = this.$store.getters.userRole
      return ['admin', 'superadmin'].includes(role)
    },
  },
  created() {
    this.fetchDeliveryFee()
  },
  methods: {
    async fetchDeliveryFee() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/core/parameters/delivery-fee/config/')
        this.deliveryFee = data.frais_societe_livraison || 0
        this.currentFee = data.frais_societe_livraison || 0
      } catch (error) {
        console.error('Error fetching delivery fee:', error)
        this.$bvToast.toast('Erreur lors du chargement des paramètres', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },
    async saveDeliveryFee() {
      if (this.deliveryFee < 0) {
        this.$bvToast.toast('Le montant ne peut pas être négatif', {
          title: 'Erreur',
          variant: 'warning',
        })
        return
      }

      this.loading = true
      try {
        await axios.put('/api/core/parameters/delivery-fee/config/', {
          frais_societe_livraison: this.deliveryFee,
        })
        this.currentFee = this.deliveryFee
        this.$bvToast.toast('Frais de livraison enregistrés avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        console.error('Error saving delivery fee:', error)
        this.$bvToast.toast('Erreur lors de l\'enregistrement', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },
    formatCurrency(value) {
      return `${parseFloat(value).toFixed(3)} DT`
    },
  },
}
</script>

<style scoped>
.parameters-container {
  padding: 1.5rem;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header Styles */
.page-header {
  background: linear-gradient(90deg, #f7fafc 0%, #e3e8ee 100%);
  border-radius: 12px;
  padding: 2rem;
  color: #1a202c;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(227, 6, 19, 0.08);
  border: 1.5px solid #e3e8ee;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1a202c;
}

.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
  color: #e30613;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
  color: #4a5568;
}

/* Card Styles */
.config-card,
.info-card {
  margin-bottom: 2rem;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.card-header-custom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(90deg, #e30613 0%, #c10510 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px 12px 0 0;
}

.card-header-custom .icon {
  font-size: 1.5rem;
}

.card-header-custom h4 {
  margin: 0;
  font-weight: 600;
}

/* Form Styles */
.config-content {
  padding: 1.5rem;
}

.config-description {
  color: #4a5568;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.form-group-enhanced {
  margin-bottom: 1.5rem;
}

.form-group-enhanced label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input-enhanced {
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 4rem 0.75rem 1rem;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.form-input-enhanced:focus {
  border-color: #e30613;
  box-shadow: 0 0 0 3px rgba(227, 6, 19, 0.1);
}

.input-suffix {
  position: absolute;
  right: 1rem;
  font-weight: 600;
  color: #718096;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.save-btn {
  background: linear-gradient(90deg, #e30613 0%, #c10510 100%);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(227, 6, 19, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Current Value Display */
.current-value-display {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #166534;
}

.current-value-display i {
  font-size: 1.2rem;
}

/* Info Card */
.info-content {
  padding: 1.5rem;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: #4a5568;
  line-height: 1.6;
}

.info-list li i {
  color: #48bb78;
  margin-top: 0.25rem;
  flex-shrink: 0;
}
</style>
